/**
 * /app/drips — Email drip campaigns.
 *
 * One screen with two tabs:
 *   Campaigns  → list + create + edit + enable/disable
 *   Enrollments → per-campaign recipient list with pause/resume/cancel/skip
 *
 * Pro+ feature. Free tier sees a paywall card with the "Try free" upgrade nudge.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, Sparkles, Plus, Play, Pause, Trash2, X, ArrowRight, ChevronRight,
  Loader2, Users, Home, UserPlus, Bot, Clock, AlertCircle, Check,
  SkipForward, CircleSlash, Send,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const AUDIENCE_META = {
  seller:  { label: "Seller Nurture",   icon: Home,     color: "bg-emerald-100 text-emerald-700" },
  buyer:   { label: "Buyer Welcome",    icon: Users,    color: "bg-blue-100 text-blue-700" },
  capture: { label: "Capture Follow-up", icon: UserPlus, color: "bg-amber-100 text-amber-700" },
};

const STATUS_META = {
  active:         { label: "Active",         color: "text-emerald-700 bg-emerald-50" },
  paused:         { label: "Paused",         color: "text-amber-700 bg-amber-50" },
  completed:      { label: "Completed",      color: "text-[var(--ink-3)] bg-[var(--bg-2)]" },
  exited_reply:   { label: "Replied — exited", color: "text-blue-700 bg-blue-50" },
  exited_unsub:   { label: "Unsubscribed",   color: "text-[var(--ink-3)] bg-[var(--bg-2)]" },
  exited_manual:  { label: "Cancelled",      color: "text-[var(--ink-3)] bg-[var(--bg-2)]" },
};


export default function Drips() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [limit, setLimit] = useState(0);
  const [aiAllowed, setAiAllowed] = useState(false);
  const [editing, setEditing] = useState(null);   // campaign being edited (or 'new')
  const [enrollFor, setEnrollFor] = useState(null); // campaign id whose enrollments drawer is open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/drip/campaigns");
      setCampaigns(data.campaigns);
      setLimit(data.limit);
      setAiAllowed(data.ai_personalize_allowed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load campaigns");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    track("drips_page_view");
    load();
  }, [load]);

  const seedTemplates = async () => {
    try {
      const { data } = await api.post("/drip/templates/seed");
      if (data.seeded?.length) {
        toast.success(`Added ${data.seeded.length} template(s)`);
      } else {
        toast.info("Templates already added");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const isPaywalled = limit === 0;
  const activeCount = campaigns.filter((c) => c.enabled).length;

  return (
    <div className="space-y-6" data-testid="drips-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">Email drip campaigns</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Set it. Forget it. Sell.</h1>
          <p className="text-[var(--ink-2)] mt-1">
            Multi-step nurture sequences with auto-exit on reply. Sellers, buyers, captures — all on autopilot.
          </p>
        </div>
        {!isPaywalled && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-outline"
              onClick={seedTemplates}
              data-testid="drips-seed-templates"
            >
              <Sparkles size={14} /> Add starter templates
            </button>
            <button
              type="button"
              className="btn-money"
              onClick={() => setEditing("new")}
              data-testid="drips-new-campaign"
            >
              <Plus size={14} /> New campaign
            </button>
          </div>
        )}
      </div>

      {isPaywalled ? (
        <Paywall />
      ) : loading ? (
        <div className="card text-center py-16 text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin mx-auto mb-3" /> Loading…
        </div>
      ) : (
        <>
          <UsageStrip
            active={activeCount}
            limit={limit}
            aiAllowed={aiAllowed}
            total={campaigns.length}
          />
          {campaigns.length === 0 ? (
            <EmptyState onSeed={seedTemplates} onNew={() => setEditing("new")} />
          ) : (
            <div className="grid gap-4">
              {campaigns.map((c) => (
                <CampaignCard
                  key={c.id}
                  c={c}
                  onEdit={() => setEditing(c)}
                  onEnrollments={() => setEnrollFor(c)}
                  onChange={load}
                  cap={limit}
                  activeCount={activeCount}
                />
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <CampaignEditor
          campaign={editing === "new" ? null : editing}
          aiAllowed={aiAllowed}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {enrollFor && (
        <EnrollmentsDrawer
          campaign={enrollFor}
          onClose={() => { setEnrollFor(null); load(); }}
        />
      )}
    </div>
  );
}

function Paywall() {
  const nav = useNavigate();
  return (
    <div
      className="card border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/60 to-white p-10"
      data-testid="drips-paywall"
    >
      <Mail size={28} className="text-emerald-600 mb-3" />
      <h2 className="font-display font-bold text-2xl mb-2">Drip campaigns are a Pro feature.</h2>
      <p className="text-[var(--ink-2)] text-lg max-w-2xl leading-relaxed mb-5">
        Auto-nurture sellers, welcome new cash buyers, and follow up with every capture lead — all on autopilot.
        Replies automatically exit the drip and bump the lead to &quot;Hot — Buyer Interested.&quot;
      </p>
      <button
        type="button"
        className="btn-money"
        onClick={() => nav("/app/billing")}
        data-testid="drips-upgrade-cta"
      >
        Upgrade to Pro <ArrowRight size={14} />
      </button>
    </div>
  );
}

function UsageStrip({ active, limit, aiAllowed, total }) {
  return (
    <div className="card p-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
      <Stat label="Active campaigns" value={`${active}${limit === -1 ? "" : ` / ${limit}`}`} />
      <Stat label="Total" value={total} />
      <Stat label="AI personalization" value={aiAllowed ? "Enabled (Hustler+)" : "Hustler+ only"} muted={!aiAllowed} />
    </div>
  );
}

function Stat({ label, value, muted }) {
  return (
    <div className="leading-tight">
      <div className={"font-display font-bold text-lg " + (muted ? "text-[var(--ink-3)]" : "text-[var(--ink)]")}>{value}</div>
      <div className="text-xs text-[var(--ink-3)]">{label}</div>
    </div>
  );
}

function EmptyState({ onSeed, onNew }) {
  return (
    <div className="card text-center py-16 px-6" data-testid="drips-empty-state">
      <Mail size={36} className="text-[var(--ink-3)] mx-auto mb-3" />
      <h3 className="font-display font-bold text-xl mb-2">No drips yet</h3>
      <p className="text-[var(--ink-2)] mb-6 max-w-lg mx-auto">
        Start with our 3 prebuilt templates (Seller Nurture, Buyer Welcome, Capture Follow-up) — or build your own from scratch.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button type="button" className="btn-money" onClick={onSeed} data-testid="drips-empty-seed">
          <Sparkles size={14} /> Add the 3 starter templates
        </button>
        <button type="button" className="btn-outline" onClick={onNew}>
          <Plus size={14} /> Or start blank
        </button>
      </div>
    </div>
  );
}

function CampaignCard({ c, onEdit, onEnrollments, onChange, cap, activeCount }) {
  const A = AUDIENCE_META[c.audience] || AUDIENCE_META.seller;
  const Icon = A.icon;
  const atCap = cap !== -1 && activeCount >= cap && !c.enabled;

  const toggleEnabled = async () => {
    try {
      await api.patch(`/drip/campaigns/${c.id}`, { enabled: !c.enabled });
      toast.success(c.enabled ? "Paused" : "Enabled");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change state");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${c.name}"? All active enrollments will be cancelled.`)) return;
    try {
      await api.delete(`/drip/campaigns/${c.id}`);
      toast.success("Deleted");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="card p-5" data-testid={`drip-campaign-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${A.color} shrink-0`}>
            <Icon size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <div className="font-display font-bold text-lg truncate">{c.name}</div>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${c.enabled ? "bg-emerald-500 text-white" : "bg-[var(--bg-2)] text-[var(--ink-3)]"}`}>
                {c.enabled ? "Active" : "Paused"}
              </span>
              {c.template_key && (
                <span className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-medium">Template</span>
              )}
              {c.ai_personalize && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-bold">
                  <Bot size={10} /> AI
                </span>
              )}
            </div>
            <div className="text-sm text-[var(--ink-2)] leading-relaxed">{c.description || `${A.label} — ${c.steps?.length || 0} steps`}</div>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-[var(--ink-3)]">
              <span><b className="text-[var(--ink)]">{c.steps?.length || 0}</b> steps</span>
              <span><b className="text-[var(--ink)]">{c.active_enrollments}</b> active</span>
              <span><b className="text-[var(--ink)]">{c.total_enrollments}</b> total</span>
              {c.auto_enroll && <span className="inline-flex items-center gap-1"><Sparkles size={10} /> auto-enroll</span>}
              {c.auto_exit_on_reply && <span className="inline-flex items-center gap-1"><CircleSlash size={10} /> exit on reply</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            className="btn-ghost"
            onClick={onEnrollments}
            data-testid={`drip-view-enrollments-${c.id}`}
            disabled={c.total_enrollments === 0}
          >
            <Users size={13} /> {c.total_enrollments}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={onEdit}
            data-testid={`drip-edit-${c.id}`}
          >
            Edit
          </button>
          <button
            type="button"
            className={"btn-money " + (atCap ? "opacity-50 cursor-not-allowed" : "")}
            onClick={toggleEnabled}
            disabled={atCap}
            title={atCap ? `Active limit reached (${cap}). Pause another first or upgrade.` : ""}
            data-testid={`drip-toggle-${c.id}`}
          >
            {c.enabled ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Enable</>}
          </button>
          <button
            type="button"
            className="btn-ghost text-red-600"
            onClick={remove}
            data-testid={`drip-delete-${c.id}`}
            aria-label="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function CampaignEditor({ campaign, aiAllowed, onClose, onSaved }) {
  const isNew = !campaign;
  const [name, setName] = useState(campaign?.name || "");
  const [audience, setAudience] = useState(campaign?.audience || "seller");
  const [description, setDescription] = useState(campaign?.description || "");
  const [steps, setSteps] = useState(campaign?.steps?.length ? campaign.steps : [
    { day_offset: 0, subject: "", body: "", use_ai: false },
  ]);
  const [autoEnroll, setAutoEnroll] = useState(campaign?.auto_enroll ?? true);
  const [autoExit, setAutoExit] = useState(campaign?.auto_exit_on_reply ?? true);
  const [aiPersonalize, setAiPersonalize] = useState(campaign?.ai_personalize ?? false);
  const [autoRollToKey, setAutoRollToKey] = useState(campaign?.auto_roll_to_key ?? "");
  const [saving, setSaving] = useState(false);

  const updateStep = (idx, patch) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addStep = () => {
    const lastDay = steps[steps.length - 1]?.day_offset || 0;
    setSteps([...steps, { day_offset: lastDay + 3, subject: "", body: "", use_ai: false }]);
  };
  const removeStep = (idx) => {
    if (steps.length === 1) return;
    setSteps(steps.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (steps.some((s) => !s.subject.trim() || !s.body.trim())) {
      toast.error("Each step needs a subject and a body");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name, audience, description, steps,
        auto_enroll: autoEnroll, auto_exit_on_reply: autoExit, ai_personalize: aiPersonalize,
        auto_roll_to_key: autoRollToKey || null,
      };
      if (isNew) {
        await api.post("/drip/campaigns", payload);
      } else {
        await api.patch(`/drip/campaigns/${campaign.id}`, payload);
      }
      toast.success(isNew ? "Created" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="drip-editor"
      >
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)] sticky top-0 bg-white">
          <div className="font-display font-bold text-xl">{isNew ? "New campaign" : "Edit campaign"}</div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name">
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Cold Phoenix Sellers Q1"
                data-testid="drip-editor-name"
              />
            </Field>
            <Field label="Audience">
              <select
                className="input"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                disabled={!isNew && !!campaign?.template_key}
                data-testid="drip-editor-audience"
              >
                <option value="seller">Sellers (motivated-seller leads)</option>
                <option value="buyer">Buyers (Power Team cash buyers)</option>
                <option value="capture">Captures (form leads from public reports)</option>
              </select>
            </Field>
          </div>
          <Field label="Description (internal)">
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Why this campaign exists"
            />
          </Field>

          <div className="flex flex-wrap gap-4 pt-2">
            <Toggle label="Auto-enroll new recipients" value={autoEnroll} onChange={setAutoEnroll} testid="drip-toggle-auto-enroll" />
            <Toggle label="Exit on reply" value={autoExit} onChange={setAutoExit} testid="drip-toggle-auto-exit" />
            <Toggle
              label="AI personalize every step"
              value={aiPersonalize && aiAllowed}
              onChange={(v) => setAiPersonalize(v && aiAllowed)}
              disabled={!aiAllowed}
              hint={!aiAllowed ? "Hustler+ only" : null}
              testid="drip-toggle-ai-personalize"
            />
          </div>

          {audience === "seller" && (
            <div
              className="pt-2 flex items-start gap-3 p-3 rounded-lg border-2 border-emerald-200 bg-emerald-50/30"
              data-testid="drip-auto-roll-row"
            >
              <Toggle
                label=""
                value={!!autoRollToKey}
                onChange={(v) => setAutoRollToKey(v ? "cold-seller-annual-v1" : "")}
                testid="drip-toggle-auto-roll"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">
                  Auto-roll silent leads into the 90-day long-haul
                </div>
                <div className="text-[11px] text-[var(--ink-2)] mt-0.5">
                  When a lead finishes this drip without replying,
                  automatically enroll them into <b>Cold Seller — 90-Day
                  Long-Haul</b>. Total touch window becomes ~111 days,
                  fully hands-off. Requires the target drip to be enabled.
                </div>
              </div>
            </div>
          )}

          <hr className="border-[var(--line)]" />

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-lg">Steps</h3>
              <button type="button" className="btn-outline text-sm" onClick={addStep} data-testid="drip-add-step">
                <Plus size={13} /> Add step
              </button>
            </div>
            <p className="text-xs text-[var(--ink-3)] mb-4">
              Available merge fields: <code className="bg-[var(--bg-2)] px-1 rounded">{`{first_name}`}</code> <code className="bg-[var(--bg-2)] px-1 rounded">{`{property_address}`}</code> <code className="bg-[var(--bg-2)] px-1 rounded">{`{city}`}</code> <code className="bg-[var(--bg-2)] px-1 rounded">{`{agent_name}`}</code>
            </p>
            <div className="space-y-3">
              {steps.map((s, idx) => (
                <div key={idx} className="border border-[var(--line)] rounded-xl p-4 bg-white" data-testid={`drip-step-${idx}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold">Step {idx + 1}</span>
                      <label className="flex items-center gap-1 text-xs text-[var(--ink-2)]">
                        <Clock size={12} /> Day
                        <input
                          type="number"
                          className="input w-20 text-sm"
                          min={0}
                          max={365}
                          value={s.day_offset}
                          onChange={(e) => updateStep(idx, { day_offset: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                        />
                      </label>
                    </div>
                    <button
                      type="button"
                      className="btn-ghost text-red-600"
                      onClick={() => removeStep(idx)}
                      aria-label="Remove step"
                      disabled={steps.length === 1}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <input
                    className="input mb-2"
                    placeholder="Subject"
                    value={s.subject}
                    onChange={(e) => updateStep(idx, { subject: e.target.value })}
                  />
                  <textarea
                    className="input min-h-[100px]"
                    placeholder="Email body — keep it human, short, specific."
                    value={s.body}
                    onChange={(e) => updateStep(idx, { body: e.target.value })}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-[var(--line)] sticky bottom-0 bg-white">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-money"
            onClick={save}
            disabled={saving}
            data-testid="drip-editor-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-[var(--ink-3)] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, value, onChange, disabled, hint, testid }) {
  return (
    <label className={"inline-flex items-center gap-2 cursor-pointer " + (disabled ? "opacity-50 cursor-not-allowed" : "")}>
      <input
        type="checkbox"
        className="w-4 h-4 accent-emerald-600"
        checked={!!value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
      />
      <span className="text-sm">{label}</span>
      {hint && <span className="text-xs text-[var(--ink-3)]">({hint})</span>}
    </label>
  );
}

function EnrollmentsDrawer({ campaign, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/drip/campaigns/${campaign.id}/enrollments`);
      setRows(data.enrollments);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load enrollments");
    }
    setLoading(false);
  }, [campaign.id]);
  useEffect(() => { load(); }, [load]);

  const act = async (eid, action) => {
    try {
      await api.post(`/drip/enrollments/${eid}/${action}`);
      toast.success({
        pause: "Paused", resume: "Resumed", cancel: "Cancelled", skip: "Skipped step",
      }[action] || "OK");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="drip-enrollments-drawer"
      >
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)] sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">Enrollments</div>
            <div className="font-display font-bold text-lg">{campaign.name}</div>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="text-center py-12 text-[var(--ink-3)]"><Loader2 size={18} className="animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-[var(--ink-3)]">
              No enrollments yet. {campaign.auto_enroll ? "New matching recipients will auto-enroll here." : "Manually enroll from a lead's page."}
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((e) => {
                const S = STATUS_META[e.status] || { label: e.status, color: "text-[var(--ink-3)] bg-[var(--bg-2)]" };
                return (
                  <div key={e.id} className="border border-[var(--line)] rounded-lg p-3" data-testid={`enrollment-${e.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display font-bold truncate">{e.recipient_email}</div>
                        <div className="text-xs text-[var(--ink-3)] mt-0.5">
                          Step {e.current_step + 1} · Sent {e.sent_count} · {new Date(e.enrolled_at).toLocaleDateString()}
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full whitespace-nowrap ${S.color}`}>
                        {S.label}
                      </span>
                    </div>
                    {e.status === "active" && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        <button type="button" className="btn-ghost text-xs" onClick={() => act(e.id, "pause")}>
                          <Pause size={11} /> Pause
                        </button>
                        <button type="button" className="btn-ghost text-xs" onClick={() => act(e.id, "skip")}>
                          <SkipForward size={11} /> Skip step
                        </button>
                        <button type="button" className="btn-ghost text-xs text-red-600" onClick={() => act(e.id, "cancel")}>
                          <X size={11} /> Cancel
                        </button>
                      </div>
                    )}
                    {e.status === "paused" && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        <button type="button" className="btn-ghost text-xs" onClick={() => act(e.id, "resume")}>
                          <Play size={11} /> Resume
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
